"use client";

import Link from "next/link";
import { useI18n } from "@/lib/i18n/I18nProvider";
import type { Dictionary } from "@/lib/i18n/dictionaries";
import { LanguageSwitcher } from "./LanguageSwitcher";

export type NavigationPanelKind =
  | "products"
  | "solutions"
  | "developers"
  | "mobile";

// Structure (ids and destinations) lives here; every visible label and
// description comes from the dictionary (panel.entries / panel.columns).
type EntryId = keyof Dictionary["panel"]["entries"];
type ColumnId = keyof Dictionary["panel"]["columns"];
type EntryDef = { id: EntryId; href: string; badge?: boolean };
type ColumnDef = { id: ColumnId; items: EntryDef[] };
type Entry = { label: string; href: string; description: string; badge?: boolean };
type Column = { title: string; items: Entry[] };

const e = (id: EntryId, href: string, badge?: boolean): EntryDef => ({
  id,
  href,
  badge,
});

const compute: ColumnDef = {
  id: "compute",
  items: [
    e("vms", "/products/virtual-machines"),
    e("containers", "/products/containers"),
    e("functions", "/products/functions"),
  ],
};
const network: ColumnDef = {
  id: "network",
  items: [
    e("networking", "/products/networking"),
    e("dns", "/products/dns"),
    e("infrastructure", "/infrastructure"),
  ],
};
const operations: ColumnDef = {
  id: "operate",
  items: [
    e("cloudShell", "/products/cloud-shell", true),
    e("consoleGuide", "/docs/dashboard"),
    e("status", "/status"),
  ],
};
const workloads: ColumnDef = {
  id: "workloads",
  items: [
    e("webApps", "/solutions#web-apps"),
    e("devEnvs", "/solutions#development"),
    e("databases", "/solutions#databases"),
  ],
};
const planning: ColumnDef = {
  id: "planning",
  items: [
    e("migration", "/solutions#migration"),
    e("networkDesign", "/docs/networking"),
    e("pricing", "/pricing"),
  ],
};
const business: ColumnDef = {
  id: "team",
  items: [
    e("business", "/business"),
    e("infraOverview", "/infrastructure"),
    e("discuss", "/contact"),
  ],
};
const gettingStarted: ColumnDef = {
  id: "gettingStarted",
  items: [
    e("quickstart", "/docs/getting-started"),
    e("vmGuide", "/docs/vps"),
    e("containerGuide", "/docs/games"),
    e("functionGuide", "/docs/functions"),
  ],
};
const developerOperations: ColumnDef = {
  id: "build",
  items: [
    e("shellGuide", "/docs/cloud-shell", true),
    e("networkingGuide", "/docs/networking"),
    e("dnsGuide", "/docs/domains"),
  ],
};
const references: ColumnDef = {
  id: "reference",
  items: [
    e("api", "/docs/api"),
    e("consoleRef", "/docs/dashboard"),
    e("faq", "/docs/faq"),
  ],
};
const sectionDefs = {
  products: { href: "/products", columns: [compute, network, operations] },
  solutions: { href: "/solutions", columns: [workloads, planning, business] },
  developers: {
    href: "/docs",
    columns: [gettingStarted, developerOperations, references],
  },
} as const;
const siteColumnDefs: ColumnDef[] = [
  compute,
  network,
  { id: "siteSolutions", items: [...workloads.items, planning.items[0]!] },
  {
    id: "siteDevelopers",
    items: [
      e("docs", "/docs"),
      developerOperations.items[0]!,
      references.items[0]!,
      references.items[2]!,
    ],
  },
  {
    id: "platform",
    items: [
      business.items[0]!,
      planning.items[2]!,
      operations.items[2]!,
      business.items[2]!,
    ],
  },
];

function localize(def: ColumnDef, t: Dictionary["panel"]): Column {
  return {
    title: t.columns[def.id],
    items: def.items.map(({ id, href, badge }) => {
      const [label, description] = t.entries[id];
      return { label, description, href, badge };
    }),
  };
}

function DirectoryLink({
  item,
  badge,
  onNavigate,
}: {
  item: Entry;
  badge: string;
  onNavigate: () => void;
}) {
  // Native fragment links preserve browser scrolling and history when selecting
  // several sections of the same page, without reusing a cached router hash.
  const EntryLink = item.href.includes("#") ? "a" : Link;
  return (
    <EntryLink className="ec-directory-link" href={item.href} onClick={onNavigate}>
      <span className="ec-directory-heading">
        <span
          className={`ec-directory-name${item.badge ? " ec-directory-name-shell" : ""}`}
        >
          {item.label}
        </span>
        {item.badge && <span className="ec-directory-badge">{badge}</span>}
      </span>
      <span className="ec-directory-description">{item.description}</span>
    </EntryLink>
  );
}

export function NavigationPanel({
  kind,
  onNavigate,
  onLogin,
  onSignup,
}: {
  kind: NavigationPanelKind;
  onNavigate: () => void;
  onLogin: () => void;
  onSignup: () => void;
}) {
  const { t: dict } = useI18n();
  const t = dict.panel;
  const site = kind === "mobile";
  const sections = (Object.keys(sectionDefs) as (keyof typeof sectionDefs)[]).map(
    (key) => ({
      key,
      href: sectionDefs[key].href,
      title: t.sections[key].title,
      action: t.sections[key].action,
      columns: sectionDefs[key].columns.map((c) => localize(c, t)),
    }),
  );
  const section = site ? null : sections.find((s) => s.key === kind)!;
  const columns =
    section?.columns ?? siteColumnDefs.map((c) => localize(c, t));
  return (
    <nav
      id={site ? "ec-mobile-menu" : `ec-${kind}-menu`}
      className={`ec-directory-panel ${site ? "ec-directory-site" : ""}`}
      aria-label={
        site || !section ? t.mobileNav : t.menuNav[section.key]
      }
    >
      <div className="ec-directory-grid">
        {columns.map((column) => (
          <section className="ec-directory-column" key={column.title}>
            <h2>{column.title}</h2>
            {column.items.map((item) => (
              <DirectoryLink
                key={item.href}
                item={item}
                badge={t.newBadge}
                onNavigate={onNavigate}
              />
            ))}
          </section>
        ))}
      </div>
      {site && (
        <div className="ec-directory-mobile">
          {sections.map((group) => (
            <details key={group.key}>
              <summary>
                {group.title}
                <span aria-hidden="true">+</span>
              </summary>
              <Link
                className="ec-directory-all"
                href={group.href}
                onClick={onNavigate}
              >
                {group.action}
              </Link>
              {group.columns.map((column) => (
                <div className="ec-directory-mobile-group" key={column.title}>
                  <h3>{column.title}</h3>
                  {column.items.map((item) => (
                    <DirectoryLink
                      key={item.href}
                      item={item}
                      badge={t.newBadge}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              ))}
            </details>
          ))}
          <div className="ec-directory-quicklinks">
            <Link href="/business" onClick={onNavigate}>
              {t.quick.business}
            </Link>
            <Link href="/pricing" onClick={onNavigate}>
              {t.quick.pricing}
            </Link>
            <Link href="/status" onClick={onNavigate}>
              {t.quick.status}
            </Link>
            <Link href="/contact" onClick={onNavigate}>
              {t.quick.contact}
            </Link>
          </div>
          <div className="ec-directory-lang">
            <span aria-hidden="true">{dict.language.label}</span>
            <LanguageSwitcher />
          </div>
          <div className="ec-directory-account">
            <button onClick={onLogin}>{t.login}</button>
            <button onClick={onSignup}>{t.createAccount}</button>
          </div>
        </div>
      )}
      <div className="ec-directory-footer">
        <Link
          className="ec-directory-all"
          href={section?.href ?? "/products"}
          onClick={onNavigate}
        >
          {section?.action ?? t.exploreAll}
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M4 10h12m-5-5 5 5-5 5" />
          </svg>
        </Link>
        <div>
          <Link href="/infrastructure" onClick={onNavigate}>
            {t.infrastructure}
          </Link>
          <Link href="/pricing" onClick={onNavigate}>
            {t.pricing}
          </Link>
          <Link href="/app" onClick={onNavigate}>
            {t.customerPanel}
          </Link>
        </div>
      </div>
    </nav>
  );
}
